import React from 'react';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EmailIcon from '@mui/icons-material/Email';
import HistoryIcon from '@mui/icons-material/History';
import ImageIcon from '@mui/icons-material/Image';
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import CaseEmptyRow from './CaseEmptyRow';
import CaseRelatedCard from './CaseRelatedCard';
import { CampaignCase } from './types';

interface Props {
  caseItem: CampaignCase;
}

const headerCellSx = { fontWeight: 'bold', whiteSpace: 'nowrap' } as const;

export default function CaseRelatedTab({ caseItem }: Props) {
  const { emails, history, attachments } = caseItem;

  return (
    <Stack spacing={1.5}>
      <CaseRelatedCard
        icon={<EmailIcon fontSize="small" />}
        iconColor="primary.main"
        title={`Emails (${emails.length})`}
        hasViewAll={emails.length > 0}
      >
        <Table size="small" aria-label="Case emails">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Subject</TableCell>
              <TableCell sx={headerCellSx}>From Address</TableCell>
              <TableCell sx={headerCellSx}>To Address</TableCell>
              <TableCell sx={headerCellSx}>Message Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.length === 0 && <CaseEmptyRow colSpan={4} message="No emails yet." />}
            {emails.map((email) => (
              <TableRow key={`${email.date}-${email.subject}`}>
                <TableCell>
                  <MuiLink component="span" underline="hover">
                    {email.subject}
                  </MuiLink>
                </TableCell>
                <TableCell>
                  <MuiLink href={`mailto:${email.from}`} underline="hover">
                    {email.from}
                  </MuiLink>
                </TableCell>
                <TableCell>{email.to}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{email.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CaseRelatedCard>

      <CaseRelatedCard
        icon={<ChatBubbleIcon fontSize="small" />}
        iconColor="success.main"
        title="Case Comments (0)"
        actionLabel="New"
      />

      <CaseRelatedCard
        icon={<HistoryIcon fontSize="small" />}
        iconColor="secondary.main"
        title={`Case History (${history.length})`}
        hasViewAll={history.length > 0}
      >
        <Table size="small" aria-label="Case history">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Date</TableCell>
              <TableCell sx={headerCellSx}>Field</TableCell>
              <TableCell sx={headerCellSx}>User</TableCell>
              <TableCell sx={headerCellSx}>Original Value</TableCell>
              <TableCell sx={headerCellSx}>New Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 && <CaseEmptyRow colSpan={5} message="No history yet." />}
            {history.map((entry) => (
              <TableRow key={`${entry.date}-${entry.field}`}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.date}</TableCell>
                <TableCell>{entry.field}</TableCell>
                <TableCell>
                  <MuiLink component="span" underline="hover">
                    {entry.user}
                  </MuiLink>
                </TableCell>
                <TableCell>{entry.original}</TableCell>
                <TableCell>{entry.next}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CaseRelatedCard>

      <CaseRelatedCard
        icon={<AttachFileIcon fontSize="small" />}
        iconColor="text.secondary"
        title={`Attachments (${attachments.length})`}
        actionLabel="Upload Files"
        hasViewAll={attachments.length > 0}
      >
        {attachments.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No attachments yet.
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, p: 2 }}>
          {attachments.map((file) => (
            <Stack key={file.name} direction="row" spacing={1} alignItems="flex-start">
              <ImageIcon fontSize="small" color="action" />
              <Box>
                <MuiLink component="span" variant="body2" underline="hover">
                  {file.name}
                </MuiLink>
                <Typography variant="caption" color="text.secondary" display="block">
                  {file.date} · Attachment
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </CaseRelatedCard>
    </Stack>
  );
}
